import React from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'success'
    | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
}

const buttonVariants = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
  secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
  outline:
    'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 focus:ring-primary-500',
  ghost: 'text-gray-900 hover:bg-gray-100 focus:ring-primary-500',
  danger: 'bg-error text-white hover:bg-red-600 focus:ring-red-500',
  success: 'bg-planet-green text-white',
  // ✨ gradient = primary + gradient border
  gradient:
    'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 relative border border-transparent rounded-lg',
};

const buttonSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base leading-5',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  className,
  disabled,
  children,
  type = 'button',
  ...props
}) => {
  const isGradient = variant === 'gradient';

  return (
    <button
      className={cn(
        'btn-base inline-flex items-center justify-center gap-2 rounded-lg border-box',
        buttonVariants[variant],
        buttonSizes[size],
        loading && 'cursor-not-allowed opacity-50',
        className
      )}
      style={
        isGradient
          ? {
              border: '1px solid transparent',
              borderRadius: '0.75rem', // same as rounded-lg
              background: `
                linear-gradient(theme('colors.primary.600'), theme('colors.primary.600')) padding-box,
                // linear-gradient(104.38deg, #00A0F1 5.68%, #0083C9 95.92%) border-box,
                linear-gradient(104.38deg, color(display-p3 0.204 0.620 0.922) 5.68%, color(display-p3 0.141 0.506 0.765) 95.92%) border-box
              `,
            }
          : undefined
      }
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}

      {icon && iconPosition === 'left' && (
        <span className="flex items-center">{icon}</span>
      )}
      <span>{children}</span>
      {icon && iconPosition === 'right' && (
        <span className="flex items-center">{icon}</span>
      )}
    </button>
  );
};
